import { IMAGE_CONFIG, registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import localeEs from '@angular/common/locales/es';
import {
  ApplicationConfig,
  importProvidersFrom,
  provideZoneChangeDetection,
} from '@angular/core';
import {
  BrowserAnimationsModule,
  provideAnimations,
} from '@angular/platform-browser/animations';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter } from '@angular/router';
import { provideIcons } from '@ng-icons/core';
import {
  tablerAdjustments,
  tablerAdjustmentsAlt,
  tablerAlertHexagon,
  tablerAlertOctagon,
  tablerAlertTriangle,
  tablerArrowLeft,
  tablerArrowNarrowLeft,
  tablerArrowRight,
  tablerArrowRightBar,
  tablerAsterisk,
  tablerBell,
  tablerBook2,
  tablerBuildingHospital,
  tablerCalendar,
  tablerCheck,
  tablerCheckbox,
  tablerChevronDown,
  tablerChevronLeft,
  tablerChevronRight,
  tablerClock,
  tablerCopy,
  tablerDeviceAirpodsCase,
  tablerDownload,
  tablerEye,
  tablerEyeOff,
  tablerFileSpreadsheet,
  tablerFileTypePdf,
  tablerFingerprint,
  tablerFoldDown,
  tablerFoldUp,
  tablerGripHorizontal,
  tablerHeartbeat,
  tablerInfoCircle,
  tablerLink,
  tablerLoader,
  tablerLoader2,
  tablerLoaderQuarter,
  tablerMapPin,
  tablerMessage,
  tablerMicroscope,
  tablerMoon,
  tablerPencil,
  tablerPhoto,
  tablerPill,
  tablerPlus,
  tablerSearch,
  tablerStethoscope,
  tablerSun,
  tablerTrash,
  tablerUser,
  tablerWorld,
  tablerX,
} from '@ng-icons/tabler-icons';
import { tablerPillFill } from '@ng-icons/tabler-icons/fill';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import {
  TRANSLATE_HTTP_LOADER_CONFIG,
  TranslateHttpLoader,
} from '@ngx-translate/http-loader';
import { CalendarModule, DateAdapter } from 'angular-calendar';
import { adapterFactory } from 'angular-calendar/date-adapters/date-fns';
import {
  createInterceptorCondition,
  INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
  IncludeBearerTokenCondition,
  includeBearerTokenInterceptor,
} from 'keycloak-angular';
import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { errorInterceptor } from './core/interceptors/error.interceptor';
import { ApiModule } from './core/services/api/api.module';
import { provideKeycloakAngular } from './keycloak.config';

registerLocaleData(localeEs, 'es');

const endpoint = environment.endpoint;
const escapedEndpoint = endpoint.replace(/[.*+?^${}()|[\\\]]/g, '\\$&');
const apiUrlRegex = new RegExp(`^${escapedEndpoint}/api.*$`, 'i');

const apiUrlsCondition =
  createInterceptorCondition<IncludeBearerTokenCondition>({
    urlPattern: apiUrlRegex,
    bearerPrefix: 'Bearer',
  });

export const appConfig: ApplicationConfig = {
  providers: [
    provideKeycloakAngular(),
    provideHttpClient(
      withInterceptors([includeBearerTokenInterceptor, errorInterceptor])
    ),
    {
      provide: INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
      useValue: [apiUrlsCondition],
    },
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideHttpClient(),
    {
      provide: TRANSLATE_HTTP_LOADER_CONFIG,
      useValue: {
        prefix: '/assets/i18n/',
        suffix: '.json',
      },
    },
    importProvidersFrom([
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useClass: TranslateHttpLoader,
        },
      }),
    ]),
    provideIcons({
      tablerBell,
      tablerLink,
      tablerPill,
      tablerAlertOctagon,
      tablerArrowLeft,
      tablerArrowRight,
      tablerPencil,
      tablerTrash,
      tablerPlus,
      tablerDeviceAirpodsCase,
      tablerBuildingHospital,
      tablerChevronLeft,
      tablerChevronRight,
      tablerAdjustments,
      tablerAlertTriangle,
      tablerX,
      tablerCheck,
      tablerLoader2,
      tablerLoaderQuarter,
      tablerLoader,
      tablerSearch,
      tablerInfoCircle,
      tablerDownload,
      tablerAsterisk,
      tablerArrowRightBar,
      tablerEye,
      tablerEyeOff,
      tablerFoldDown,
      tablerFoldUp,
      tablerFingerprint,
      tablerCalendar,
      tablerAdjustmentsAlt,
      tablerClock,
      tablerMapPin,
      tablerAlertHexagon,
      tablerUser,
      tablerMessage,
      tablerStethoscope,
      tablerCheckbox,
      tablerArrowNarrowLeft,
      tablerCopy,
      tablerGripHorizontal,
      tablerBook2,
      tablerPhoto,
      tablerSun,
      tablerMoon,
      tablerChevronDown,
      tablerMicroscope,
      tablerWorld,
      tablerPillFill,
      tablerHeartbeat,
      tablerFileTypePdf,
      tablerFileSpreadsheet,
    }),
    provideAnimationsAsync(),
    provideAnimations(),
    BrowserAnimationsModule,
    importProvidersFrom(
      CalendarModule.forRoot({
        provide: DateAdapter,
        useFactory: adapterFactory,
      }),
      ApiModule.forRoot({
        rootUrl: endpoint,
      })
    ),
    {
      provide: IMAGE_CONFIG,
      useValue: {
        disableImageSizeWarning: true,
        disableImageLazyLoadWarning: true,
      },
    },
  ],
};
